import { useEffect, useRef, useState } from 'react';
import { Modal } from '../../components/Modal';
import { Button } from '../../components/Button';
import { patchUser, ApiError, type PatchUserBody } from '../../lib/api';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../contexts/AuthContext';
import { PS, T } from '../../lib/theme';
import { fullName } from '../../lib/format';
import type { AdminLookupUser } from '../../lib/types';

interface Props {
  open: boolean;
  user: AdminLookupUser;
  onClose: () => void;
  onSaved: () => void;
}

const inputStyle = {
  padding: '8px 10px',
  border: `1px solid ${PS.border}`,
  borderRadius: 3,
  minHeight: 40,
  ...T.ui,
} as const;

export function EditUserModal({ open, user, onClose, onSaved }: Props) {
  const { push } = useToast();
  const { adminUser } = useAuth();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [busy, setBusy] = useState(false);
  const firstNameRef = useRef<HTMLInputElement>(null);

  // Editing your own account must not let you drop your own admin rights
  // (the server enforces this too).
  const isSelf = adminUser?.id === user.id;

  useEffect(() => {
    if (!open) return;
    setFirstName(user.first_name);
    setLastName(user.last_name ?? '');
    setEmail(user.email ?? '');
    setPhone(user.phone ?? '');
    setIsAdmin(user.is_admin);
    setBusy(false);
  }, [open, user]);

  // Focus the first name field on open instead of using the autoFocus prop.
  useEffect(() => {
    if (open) {
      firstNameRef.current?.focus();
    }
  }, [open]);

  const handleSave = async () => {
    const trimmedName = firstName.trim();
    const trimmedLast = lastName.trim();
    if (!trimmedName) {
      push('error', 'First name is required.');
      return;
    }
    const body: PatchUserBody = {
      first_name: trimmedName,
      // Optional on edit so a legacy user without a last name isn't blocked from
      // other edits. The API rejects a blank string (422), so only send it when
      // non-empty; omitting it leaves the stored value untouched.
      ...(trimmedLast ? { last_name: trimmedLast } : {}),
      email: email.trim() || null,
      phone: phone.trim() || null,
      is_admin: isAdmin,
    };
    setBusy(true);
    try {
      await patchUser(user.id, body);
      push('success', 'User updated.');
      onClose();
      onSaved();
    } catch (err) {
      push('error', err instanceof ApiError ? err.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={`Edit ${fullName(user)}`}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={T.label}>First name</span>
          <input ref={firstNameRef} value={firstName} onChange={(e) => setFirstName(e.target.value)} style={inputStyle} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={T.label}>Last name</span>
          <input value={lastName} onChange={(e) => setLastName(e.target.value)} style={inputStyle} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={T.label}>Email</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} style={inputStyle} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={T.label}>Phone (E.164)</span>
          <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+995591234567" style={inputStyle} />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="checkbox"
            checked={isAdmin}
            disabled={isSelf}
            onChange={(e) => setIsAdmin(e.target.checked)}
          />
          <span style={T.label}>Admin</span>
        </label>
        {isSelf && (
          <p style={{ ...T.caption, margin: 0 }}>You cannot change your own admin status.</p>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={() => void handleSave()} disabled={busy}>
            {busy ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
